import { useEffect } from "react";
import { VLInput } from "views/uikit/FormFields/VLInput/VLInput";
import { Form, Flex, Modal } from "antd";
import { UserData } from "./ChangeUserRoleModal";
import { useUserModalActions } from "./useUserModals";
import { VLInputWithPassword } from "views/uikit/FormFields/VLInputWithPassword";
import { VLModalTitle } from "views/uikit/VLModalTitle/VLModalTitle";
import styles from "./style.module.scss";

interface ResetUserPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserData | null;
}

const FORM_ID = "reset-user-password-form";

const ResetUserPasswordModal = ({
  isOpen,
  onClose,
  user,
}: ResetUserPasswordModalProps) => {
  const { resetUserPassword, loading } = useUserModalActions();
  const [form] = Form.useForm();

  useEffect(() => {
    if (isOpen && user) {
      form.setFieldsValue({
        username: user.username,
      });
    } else {
      form.resetFields();
    }
  }, [user, isOpen, form]);

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleSave = async (values: { password: string }) => {
    if (user) {
      await resetUserPassword({ userId: user.id, password: values.password });
      handleCancel();
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      onOk={() => {
        form.submit();
      }}
      okText="Save"
      closable={true}
      confirmLoading={loading}
      destroyOnHidden
      title={
        <VLModalTitle
          title="Reset Password"
          subtitle="Enter a new password for the user."
        />
      }
    >
      <Form
        form={form}
        id={FORM_ID}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{
          username: user?.username || "",
        }}
      >
        <Flex vertical gap={16}>
          <Form.Item name="username" label="User Name">
            <VLInput disabled />
          </Form.Item>

          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true, message: "Please input the new password" },
              { min: 8, message: "Password must be at least 8 characters" },
            ]}
          >
            <VLInputWithPassword className={styles.inputWithPassword} />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={["password"]}
            rules={[
              {
                required: true,
                message: "Please confirm your password",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <VLInputWithPassword className={styles.inputWithPassword} />
          </Form.Item>
        </Flex>
      </Form>
    </Modal>
  );
};

export default ResetUserPasswordModal;
